export const parseColor = (colorStr) => {
  if (!colorStr || typeof colorStr !== "string") {
    return null;
  }
  const str = colorStr.trim();

  // #RGB or #RGBA
  if (/^#([0-9a-fA-F]{3,4})$/.test(str)) {
    const hex = str.slice(1);
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    const a = hex.length === 4 ? parseInt(hex[3] + hex[3], 16) / 255 : 1;
    return { r, g, b, a };
  }

  // #RRGGBB or #RRGGBBAA
  if (/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(str)) {
    const hex = str.slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
    return { r, g, b, a };
  }

  // rgb(r, g, b) or rgba(r, g, b, a)
  const rgbMatch = str.match(
    /^rgba?\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([\d.]+))?\s*\)$/i
  );
  if (rgbMatch) {
    const r = Math.min(255, Math.max(0, parseInt(rgbMatch[1], 10)));
    const g = Math.min(255, Math.max(0, parseInt(rgbMatch[2], 10)));
    const b = Math.min(255, Math.max(0, parseInt(rgbMatch[3], 10)));
    const a = rgbMatch[4] !== undefined ? Math.min(1, Math.max(0, parseFloat(rgbMatch[4]))) : 1;
    return { r, g, b, a };
  }

  return null;
};

export const blendRgbaOverHex = (foreground, background, fallbackBg = "#FFFFFF") => {
  const fg = parseColor(foreground);
  let bg = parseColor(background);

  if (!bg) {
    bg = parseColor(fallbackBg) || { r: 255, g: 255, b: 255, a: 1 };
  }

  if (!fg) {
    return bg;
  }

  const alpha = fg.a;
  return {
    r: Math.round(fg.r * alpha + bg.r * (1 - alpha)),
    g: Math.round(fg.g * alpha + bg.g * (1 - alpha)),
    b: Math.round(fg.b * alpha + bg.b * (1 - alpha)),
    a: 1,
  };
};

export const getRelativeLuminance = (rgb) => {
  if (!rgb) return 0;
  const transform = (val) => {
    const s = val / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const r = transform(rgb.r);
  const g = transform(rgb.g);
  const b = transform(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const getContrastRatio = (foreground, background, fallbackBg = "#FFFFFF") => {
  const fgParsed = parseColor(foreground);
  const bgParsed = parseColor(background);

  if (!fgParsed && !bgParsed) {
    return 1.0;
  }

  const fgEffective = blendRgbaOverHex(foreground, background || fallbackBg, fallbackBg);
  const bgEffective = blendRgbaOverHex(background || fallbackBg, fallbackBg, "#FFFFFF");

  const l1 = getRelativeLuminance(fgEffective);
  const l2 = getRelativeLuminance(bgEffective);

  const brighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  const ratio = (brighter + 0.05) / (darker + 0.05);
  return Math.round(ratio * 100) / 100;
};

export const isContrastApproved = (foreground, background, options = {}) => {
  const { isLargeText = false, minRatio } = options;
  const threshold = minRatio ?? (isLargeText ? 3.0 : 4.5);
  return getContrastRatio(foreground, background) >= threshold;
};
